import { forkJoin, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '@bo-client/config/config.service';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { map, switchMap } from 'rxjs/operators';
import { AutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/autocomplete.strategy';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

interface GetCityResult {
  city: string;
  cityId: string;
  orgEinheit: string;
  district: string;
  zipCode: string;
}

interface GetStreetResult {
  city: string;
  cityId: string;
  orgEinheit: string;
  zipCode: string;
  street: string;
  streetId: string;
  sapDistrictNumber: string;
}

export class AdressSenseServiceAutocompleteStrategy implements AutocompleteStrategy {
  constructor(
    public httpClientBack: HttpClient,
    public configService: ConfigService,
    public dataShareService: DataShareService
  ) {}

  getCitySuggestions(lookup: string): Observable<GisDataModel[]> {
    return this.httpClientBack
      .get<GetCityResult>(
        `${this.configService.getSettings('address-sense-service')}/search/cities?plz_city_lookup=${lookup}`,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((adresses: any) => {
          return adresses.map((address) => ({
            city: address.city + (address.district ? ' - ' + address.district : ''),
            cityId: address.cityId,
            orgEinheit: address.orgEinheit,
            zipCode: address.zipCode
          }));
        })
      );
  }

  getStreetSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    return this.httpClientBack
      .get<GetStreetResult>(
        `${this.configService.getSettings(
          'address-sense-service'
        )}/search/streets?street_lookup=${val}&city_id=${selectedGisData.cityId}&limit=20&zipCode=${
          selectedGisData.zipCode
        }&city_name=${selectedGisData.city}`,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((adresses: any) => {
          return adresses.map((address) => ({
            city: address.city,
            cityId: address.cityId,
            orgEinheit: address.orgEinheit,
            zipCode: address.zipCode,
            sapDistrictNumber: address.sapDistrictNumber,
            street: address.street,
            streetId: address.streetId,
            originalData: address
          }));
        })
      );
  }

  getStreetNoSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    const useAvailabilityEndpoint =
      this.dataShareService.tenantConfig?.common?.useAvailabilityEndpointForAreas === true;

    return this.httpClientBack
      .get<GetStreetResult>(
        `${this.configService.getSettings(
          'address-sense-service'
        )}/search/houseNo?houseNo_lookup=${val}&city_id=${selectedGisData.cityId}&limit=20&zipCode=${
          selectedGisData.zipCode
        }&street_id=${selectedGisData.streetId}`,
        { headers: this.getHeaders() }
      )
      .pipe(
        switchMap((adresses: any) => {
          const houseNoList = adresses.map((address) => ({
            city: address.city,
            cityId: address.cityId,
            orgEinheit: address.orgEinheit,
            zipCode: address.zipCode,
            sapDistrictNumber: address.sapDistrictNumber,
            originalData: address,
            houseNo: address.houseNo,
            houseNoAddition: address.houseNoAddition,
            availableAreas: address.areas,
            latitude: address.latitude,
            longitude: address.longitude
          }));

          if (!useAvailabilityEndpoint || houseNoList.length === 0) {
            return of(houseNoList);
          }

          const baseUrl = this.configService.getSettings('address-sense-service');
          const sources = houseNoList.map((h) => {
            if (h.longitude != null && h.latitude != null) {
              return this.httpClientBack.get<any>(
                `${baseUrl}/availability?longitude=${h.longitude}&latitude=${h.latitude}&radius=400`,
                { headers: this.getHeaders() }
              );
            }
            return of<any[]>([]);
          });
          return forkJoin(sources).pipe(
            map((resPerHouse: any[][]) => {
              houseNoList.forEach((houseNo, idx) => {
                if (houseNo.longitude != null && houseNo.latitude != null) {
                  houseNo.availableAreas = this.mapAvailabilityAreasToEnumValues(resPerHouse[idx]);
                }
              });
              return houseNoList;
            })
          );
        })
      );
  }

  /**
   * Überprüft, ob eine spezifische Area verfügbar ist.
   *
   * @param areaType - Der Typ der Area, die geprüft werden soll.
   * @param selectedOption - Das ausgewählte GisDataModel.
   * @return boolean - Gibt true zurück, wenn die Area verfügbar ist, sonst false.
   */
  isAvailableArea(areaType: AreaTypeEnum, selectedOption: GisDataModel): boolean {
    if (!selectedOption.availableAreas) {
      return false;
    }

    return selectedOption.availableAreas?.some((x) => areaType.toUpperCase() === x.toUpperCase());
  }

  private static readonly AVAILABILITY_AREA_MAP: Record<string, string> = {
    Strom: 'POWER',
    Gas: 'GAS',
    Wasser: 'WATER',
    Fernwärme: 'DISTRICTHEAT',
    Leerrohr: 'LWL',
    Glasfaser: 'LWLK',
    Abwasser: 'WASTEWATER',
    Nahwärme: 'LOCALHEAT'
  };

  private mapAvailabilityAreasToEnumValues(areasResult: any[]): string[] {
    if (!areasResult || areasResult.length === 0) {
      return [];
    }
    return areasResult
      .map((entry) => AdressSenseServiceAutocompleteStrategy.AVAILABILITY_AREA_MAP[entry.area] || entry.area)
      .filter(Boolean);
  }

  private getHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'X-Tenant': this.dataShareService.currentTenant.id,
      'X-Env': 'master'
    };
  }
}
